import { STATUS_PALETTE, collectVehicleAlerts, deriveStatus, speedText } from './vehicle-status'

export type TranslateFn = (key: string) => string

export interface VehiclePosition {
  imei: number | string
  lat: number
  lng: number
  visible?: boolean
  ignition?: number | boolean
  etoll?: number | string
  icon_id?: number | string
  heading?: number | string
  speed?: number | string
  device_timestamp?: number | string
  can_odometer?: number | string | null
  can_rpm?: number | string | null
  can_fuel_level_liters?: number | string | null
  name?: string
  registration?: string

  [key: string]: unknown
}

export interface LabelContext {
  sentGeoImei: Array<number>
  t: TranslateFn
}

// ignition ON: stale after 5 min, ignition OFF: stale after 40 min.
const MISSING_THRESHOLD_IGNITION_ON = 300
const MISSING_THRESHOLD_IGNITION_OFF = 2400

export function getIconSrc(iconId: unknown): string {
  try {
    return require(`@/assets/img/map/icons/${Number(iconId)}.png`)
  } catch (_) {
    return require('@/assets/img/map/icons/0.png')
  }
}

export function getVehicleName(obj: VehiclePosition): string {
  return obj.name === 'null' || obj.name === '' ? String(obj.registration ?? '') : String(obj.name ?? '')
}

export function isVehicleMissing(obj: VehiclePosition): boolean {
  const diff = Math.floor(Date.now() / 1000) - Number(obj.device_timestamp)
  return obj.ignition ? diff > MISSING_THRESHOLD_IGNITION_ON : diff > MISSING_THRESHOLD_IGNITION_OFF
}

// Last known travel direction (azimuth, deg) per vehicle. Trackers report heading 0 when
// stopped, which would snap the arrow to north; keep the last heading recorded while moving so a
// parked vehicle keeps pointing the way it was going (legacy mapv3 `vehicleHeadings` behaviour).
const lastMovingHeading = new Map<string, number>()

/**
 * Travel direction to draw, in degrees (0 = N, clockwise), or null when unknown. Updates the
 * remembered heading only while the vehicle is moving; when stopped it reuses the last moving
 * heading so the arrow doesn't reset to north.
 */
export function resolveHeading(obj: VehiclePosition): number | null {
  const key = String(obj.imei)
  const speed = Number(obj.speed)
  const heading = Number(obj.heading)
  const hasHeading = Number.isFinite(heading)

  if (Number.isFinite(speed) && speed > 0 && hasHeading) {
    lastMovingHeading.set(key, heading)
    return heading
  }

  const remembered = lastMovingHeading.get(key)
  if (remembered !== undefined) {
    return remembered
  }
  // Never seen moving: only trust a real (non-zero) azimuth, as 0 is the stopped default.
  return hasHeading && heading !== 0 ? heading : null
}

/**
 * Directional arrow drawn on the vehicle's coordinate, coloured by driving status and rotated to
 * the travel azimuth. Replaces the plain point dot whenever the heading is known.
 */
function buildArrowHtml(color: string, heading: number): string {
  return (
    `<div class="veh-pin__arrow" style="transform:rotate(${heading}deg)">` +
      '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">' +
        `<path d="M12 2.5 L19.5 20.5 L12 15.8 L4.5 20.5 Z" fill="${color}" ` +
          'stroke="#fff" stroke-width="1.6" stroke-linejoin="round"/>' +
      '</svg>' +
    '</div>'
  )
}

export function buildLabelContent(obj: VehiclePosition, ctx: LabelContext): string {
  const hasGeo = ctx.sentGeoImei.includes(Number(obj.imei))
  const hasEtoll = Number(obj.etoll) === 1
  return (
    (hasGeo ? '<span class="fa fa-globe"></span> ' : '') +
    (hasEtoll ? '<span class="fas fa-dollar-sign"></span> ' : '') +
    getVehicleName(obj)
  )
}

export function buildExtendedContent(obj: VehiclePosition, ctx: LabelContext): string {
  const row = (icon: string, value: string): string =>
    `<div class="veh-pin__tip-row"><i class="bi ${icon}"></i><span>${value}</span></div>`

  const num = (v: unknown): number | null => {
    const n = Number(v)
    return Number.isFinite(n) ? n : null
  }

  const rows: string[] = [
    row('bi-speedometer2', `${num(obj.speed) ?? 0} ${ctx.t('components.map.kph')}`),
    row('bi-key-fill', obj.ignition ? ctx.t('components.map.on') : ctx.t('components.map.off'))
  ]

  const odometer = num(obj.can_odometer)
  if (odometer !== null) rows.push(row('bi-signpost-2', `${odometer.toLocaleString('pl-PL')} km`))

  const rpm = num(obj.can_rpm)
  if (rpm !== null) rows.push(row('bi-arrow-repeat', `${rpm.toLocaleString('pl-PL')} rpm`))

  const fuel = num(obj.can_fuel_level_liters)
  if (fuel !== null) rows.push(row('bi-fuel-pump', `${fuel} L`))

  const voltage = num((obj as Record<string, unknown>).voltage)
  if (voltage !== null) rows.push(row('bi-battery-half', `${(voltage / 1000).toFixed(1)} V`))

  return rows.join('')
}

/**
 * Renders the marker as a labelled pin (bubble + stem + point), coloured by driving status
 * (drive/idle/rest/alert) — the same status semantics as the fleet list. The compact bubble
 * (status dot + icon + plate + speed) is always visible; the extended details live in an
 * absolutely-positioned `.veh-pin__tip` that slides in on hover (pure CSS — no JS swap, so the
 * pin geometry and its on-coordinate point never move).
 */
export function applyVehicleMarkerContent(el: HTMLElement, obj: VehiclePosition, ctx: LabelContext): void {
  const status = deriveStatus(obj, collectVehicleAlerts(obj, () => ''))
  const color = STATUS_PALETTE[status].color

  // Point on the coordinate: a directional arrow when the travel azimuth is known, otherwise the
  // plain dot. Both are centred on the coordinate, so the arrow's rotation never shifts the pin.
  const heading = resolveHeading(obj)
  const pointHtml = heading !== null
    ? buildArrowHtml(color, heading)
    : `<div class="veh-pin__point" style="background:${color}"></div>`

  // Manage only our own classes via classList — a full `el.className =` reset would wipe the
  // `maplibregl-marker` class MapLibre adds (position: absolute), which detaches the pin from
  // its coordinate on refresh updates. (Status colour is applied inline, so no status class.)
  el.classList.add('veh-pin')
  el.classList.toggle('veh-pin--missing', isVehicleMissing(obj))
  el.innerHTML =
    '<div class="veh-pin__bubble">' +
      '<div class="veh-pin__bar">' +
        `<span class="veh-pin__dot" style="background:${color}"></span>` +
        `<img class="veh-pin__icon" src="${getIconSrc(obj.icon_id)}" alt="" onerror="this.style.display='none'">` +
        `<span class="veh-pin__plate">${buildLabelContent(obj, ctx)}</span>` +
        `<span class="veh-pin__speed">${speedText(obj)}</span>` +
      '</div>' +
      `<div class="veh-pin__tip"><div class="veh-pin__tip-body">${buildExtendedContent(obj, ctx)}</div></div>` +
    '</div>' +
    `<div class="veh-pin__stem" style="background:${color}"></div>` +
    pointHtml
}

export function createVehicleMarkerElement(obj: VehiclePosition, ctx: LabelContext): HTMLElement {
  const el = document.createElement('div')
  applyVehicleMarkerContent(el, obj, ctx)
  return el
}
